"use client";

// WO-GF-001-P1B-S7 (W5 / P1) — SITE LINK LAYER. The document-scoped hydrator for the W5 "dead link/button"
// long-tail that lives in the captured mirror BODY + footer (same family as HubChromeLayer / PopupLayer /
// CarouselLayer): it finds the JS-dead photocopied controls and wires the real navigation onto them WITHOUT
// touching their DOM at rest, so the pixel gate stays green. Each block no-ops when its target is absent, so
// the layer is safe to mount on every desktop page. Idempotent + fully cleaned up on unmount.
//
// Blocks (all live-derived destinations, all build-served → no dangling):
//   • SECTION NAV — the non-Whistler mountain "MOUNTAIN PROFILE / TRANSPORT INFORMATION / RENTALS /
//     ACTIVITIES / WHISTLER IS LIVE — BOOK & SHOP" MUI buttons → the mountain's real sub-pages (sectionNavHref).
//   • CATEGORY NAV (S8) — the leftDock "Select Research Category" dropdown toggle → an injected menu of the 4
//     directory categories (real <a href>, display:none at rest), each → the mountain's served directory page
//     (categoryOptions). Fixes the dead toggle on 281 directory pages + un-orphans Whistler /equipment-rentals.
//   • COOKIE-CONSENT — the react-cookie-consent Accept button on the pages HubChromeLayer does NOT cover
//     (home, the 30 landings, content) → set consent + dismiss.
//
// Desktop only (F30 — mounted from page.tsx's desktop path). Behavior gates: chrome/section-nav-routes +
// directory/category-nav-routes.

import { useEffect } from "react";
import {
  CATEGORY_LABELS,
  categoryOptions,
  mountainCodeFromPath,
  sectionNavHref,
} from "./hub-nav";

// Footer category quick-links (`[mountain]`-templated on live). On pages with NO mountain context (home,
// content, wssf) the mirror left them href="#" (dead); live resolves them to the WHISTLER defaults (the
// live/flagship mountain — verified on /faq live: Mountain Transport→/mountain-whistler/bus-schedule, etc.).
// We set the real href so they match live exactly (also closes the missing footer edges in the link-graph).
const FOOTER_WHISTLER_DEFAULTS: [RegExp, string][] = [
  [/mountain transport|bus to (choose )?mountain|shuttle to (choose )?mountain|(choose )?mountain bus schedule/i, "/mountain-whistler/bus-schedule"],
  [/deals & packages/i, "/mountain-whistler/deals-and-packages"],
  [/ski rentals/i, "/mountain-whistler/ski-rentals"],
  [/snowmobile tours|snowshoe tours|dog sledding tours/i, "/mountain-whistler/activities"],
];

export default function SiteLinkLayer() {
  useEffect(() => {
    const cleanups: (() => void)[] = [];
    const code = mountainCodeFromPath(location.pathname);

    // ---- SECTION NAV (W5) — non-Whistler mountain body nav buttons → the real sub-pages. Matched by the
    // exact captured label; nothing shows at rest (the button markup is untouched → pixel gate holds). Wired
    // once per button (data-behavior guard). Only on mountain pages (code present); no-op elsewhere.
    if (code) {
      for (const btn of Array.from(
        document.querySelectorAll<HTMLElement>("button"),
      )) {
        if (btn.closest("header, footer")) continue; // body only
        if (btn.getAttribute("data-behavior")) continue; // already wired (idempotent)
        const label = (btn.textContent || "").replace(/\s+/g, " ").trim();
        const href = sectionNavHref(code, label);
        if (!href) continue;
        btn.setAttribute("data-behavior", "section-nav");
        btn.style.cursor = "pointer";
        const go = (e: Event) => {
          e.preventDefault();
          window.location.assign(href);
        };
        btn.addEventListener("click", go);
        cleanups.push(() => btn.removeEventListener("click", go));
      }
    }

    // ---- DIRECTORY CATEGORY DROPDOWN (S8 · W3/W5) — the leftDock "Select Research Category" toggle
    // (`.dropdown-toggle.btn.btn-select`, labeled by the CURRENT category) opens the 4-way directory menu on
    // live; the mirror stripped the JS so the toggle is dead (catalog #9/#12/#15/#21, 281 pp). We inject a
    // real-<a href> menu of the 4 categories → the mountain's served directory pages (categoryOptions, same
    // destinations as sectionNavHref), display:none at rest → pixel gate untouched. Matching ONLY the 4 exact
    // category labels excludes the transport widget's location selector (same class, different control). The
    // real <a href> options are crawlable, so the Whistler "EQUIPMENT RENTAL COMPANIES" → /equipment-rentals
    // edge also un-orphans that served index (+ its supplier-detail cascade). Only on mountain pages.
    if (code) {
      const CATSET = new Set<string>(CATEGORY_LABELS);
      for (const toggle of Array.from(
        document.querySelectorAll<HTMLElement>("button.dropdown-toggle.btn.btn-select"),
      )) {
        if (toggle.getAttribute("data-behavior")) continue; // idempotent
        const label = (toggle.textContent || "").replace(/\s+/g, " ").trim();
        if (!CATSET.has(label)) continue; // not a category toggle (skip transport location selector)
        const host = toggle.parentElement; // `.form-control.dropdown` — the positioning context
        if (!host || host.querySelector("[data-category-menu]")) continue;
        host.style.position = host.style.position || "relative";

        const menu = document.createElement("div");
        menu.setAttribute("data-category-menu", "true");
        menu.setAttribute("data-behavior", "category-menu");
        Object.assign(menu.style, {
          display: "none",
          position: "absolute",
          top: "calc(100% + 2px)",
          left: "0",
          zIndex: "1000",
          minWidth: "100%",
          background: "#fff",
          border: "1px solid rgba(0,0,0,.15)",
          borderRadius: "6px",
          padding: "6px 0",
          boxShadow: "0 8px 24px rgba(0,0,0,.18)",
        } satisfies Partial<CSSStyleDeclaration>);
        for (const opt of categoryOptions(code)) {
          const a = document.createElement("a");
          a.href = opt.href;
          a.textContent = opt.label;
          a.setAttribute("data-behavior", "category-option");
          Object.assign(a.style, {
            display: "block",
            padding: "6px 16px",
            color: "rgb(33,37,41)",
            textDecoration: "none",
            whiteSpace: "nowrap",
            fontSize: "14px",
          } satisfies Partial<CSSStyleDeclaration>);
          menu.appendChild(a);
        }
        host.appendChild(menu);

        toggle.setAttribute("data-behavior", "category-nav");
        toggle.setAttribute("aria-expanded", "false");
        toggle.style.cursor = "pointer";
        const openMenu = (e: Event) => {
          e.preventDefault();
          const open = menu.style.display === "none";
          menu.style.display = open ? "block" : "none";
          toggle.setAttribute("aria-expanded", String(open));
        };
        const onDoc = (e: MouseEvent) => {
          const t = e.target as Node;
          if (!menu.contains(t) && !toggle.contains(t)) {
            menu.style.display = "none";
            toggle.setAttribute("aria-expanded", "false");
          }
        };
        toggle.addEventListener("click", openMenu);
        document.addEventListener("click", onDoc);
        cleanups.push(() => {
          toggle.removeEventListener("click", openMenu);
          document.removeEventListener("click", onDoc);
          menu.remove();
        });
      }
    }

    // ---- MARKETPLACE TABS (S12 · RW2-8) — the store chrome's tab strip (`.dealContent > .dealContainer`):
    // "market place" · "snowclub perks" · "buy & Sell". All three were dead photocopies, so the founder
    // could not switch between them (re-walk 2026-07-22).
    //
    // FOUNDER RULING (b), 2026-07-22: Perks must be clickable — this SUPERSEDES the RW-7/F75 "parked for
    // Phase-2" ruling. "Buy & Sell" stays offline-looking, which is also what live serves: the mirror
    // carries it with the `disabled` class on every one of the 33 tab-bearing pages, so leaving it inert is
    // faithful, not lazy.
    //
    // The destinations are derived from the MIRROR'S OWN ACTIVE STATES rather than invented: the tab that
    // carries `active` on a page names that page's tab, so /…/deals-and-packages ⇒ "market place" and
    // /snowclub-perks ⇒ "snowclub perks". Both targets are build-served. Per-mountain perks exist ONLY for
    // Whistler (mountain-whistler_snowclub-perks); every other mountain resolves to the global
    // /snowclub-perks — the S7 footer/funnel precedent — so no tab can 404.
    {
      const tabs = Array.from(
        document.querySelectorAll<HTMLElement>(".dealContent .dealContainer"),
      );
      for (const tab of tabs) {
        if (tab.getAttribute("data-behavior")) continue; // idempotent
        const label = (tab.textContent || "").replace(/\s+/g, " ").trim().toLowerCase();
        // live serves Buy & Sell disabled — keep it inert and looking offline (founder ruling (b))
        if (tab.classList.contains("disabled") || /buy\s*&\s*sell/.test(label)) continue;
        // the tab for the page we are already on does nothing (it is the active one)
        if (tab.classList.contains("active")) continue;

        let href: string | null = null;
        if (/market\s*place/.test(label)) {
          href = code ? `/mountain-${code}/deals-and-packages` : "/deals-and-packages";
        } else if (/perks/.test(label)) {
          // Whistler is the only mountain whose per-mountain perks page is served
          href = code === "whistler" ? "/mountain-whistler/snowclub-perks" : "/snowclub-perks";
        }
        if (!href) continue;
        tab.setAttribute("data-behavior", "marketplace-tab");
        tab.setAttribute("data-nav-href", href);
        tab.style.cursor = "pointer";
        const dest = href;
        const go = (e: Event) => {
          e.preventDefault();
          window.location.assign(dest);
        };
        tab.addEventListener("click", go);
        cleanups.push(() => tab.removeEventListener("click", go));
      }
    }

    // ---- TRANSPORT-COMPANY NAV (S12 · RW2-2) — on the transport directory's "General Information" page
    // (/mountain-<code>/bus-schedule) the per-company MUI buttons ("EPIC RIDES BUS SCHEDULE", "SKYLYNX BUS
    // SCHEDULE", "WHISTLER SKIBUS BUS SCHEDULE") and "CHECK TRANSPORT OPTIONS" are dead photocopies. Founder
    // re-walk 2026-07-22: the per-company button must open THAT COMPANY'S PROFILE page, and "Check Out
    // Transport Options" must open the Show-All of transport options for the CURRENT mountain.
    //
    // The destinations are NOT constructed from the label — they are READ OFF ANCHORS THE PAGE ALREADY
    // CARRIES, so nothing is invented and nothing can dangle. The same page's "CHOOSE COMPANY" dock renders
    // real <a href> entries ("Epic Rides" → /mountain-whistler/bus-schedule/epic-rides, … , "Show All" →
    // …/show-all), each of which is a build-served mirror page. We index those anchors, then join the button
    // to one by NAME: strip the trailing " BUS SCHEDULE" from the button label and match the dock entry.
    // A button whose company has no anchor on the page is left untouched (never guessed into a URL).
    //
    // Whistler-scoped in practice, and correctly so: off-Whistler bus-schedule mirrors carry no company
    // cards, no per-company buttons and no Show-All (verified across the mirror) — the established
    // "off-Whistler has no marketplace/transport depth on live" finding (F59-F64). This block therefore
    // no-ops there rather than fabricating depth live does not have.
    if (code) {
      const norm = (s: string) => s.replace(/\s+/g, " ").trim().toUpperCase();
      const companyHref = new Map<string, string>();
      let showAllHref: string | null = null;
      for (const a of Array.from(
        document.querySelectorAll<HTMLAnchorElement>(
          `a[href^="/mountain-${code}/bus-schedule/"]`,
        ),
      )) {
        const href = a.getAttribute("href");
        if (!href) continue;
        const label = norm(a.textContent || "");
        if (!label) continue;
        if (href.endsWith("/show-all")) {
          showAllHref = href;
          continue;
        }
        if (href.endsWith("/airport-information")) continue; // its own control
        if (label === "VIEW PROFILE") continue; // the card CTA, not the company name
        if (!companyHref.has(label)) companyHref.set(label, href);
      }

      for (const btn of Array.from(
        document.querySelectorAll<HTMLElement>("button"),
      )) {
        if (btn.closest("header, footer")) continue; // body only
        if (btn.getAttribute("data-behavior")) continue; // idempotent
        const label = norm(btn.textContent || "");
        let href: string | null = null;
        let behavior: string | null = null;
        if (label === "CHECK TRANSPORT OPTIONS") {
          href = showAllHref;
          behavior = "transport-showall-nav";
        } else if (label.endsWith(" BUS SCHEDULE")) {
          href = companyHref.get(label.replace(/ BUS SCHEDULE$/, "")) ?? null;
          behavior = "transport-company-nav";
        }
        if (!href || !behavior) continue; // no real anchor to join → leave dead, never invent
        btn.setAttribute("data-behavior", behavior);
        btn.setAttribute("data-nav-href", href);
        btn.style.cursor = "pointer";
        const dest = href;
        const go = (e: Event) => {
          e.preventDefault();
          window.location.assign(dest);
        };
        btn.addEventListener("click", go);
        cleanups.push(() => btn.removeEventListener("click", go));
      }
    }

    // ---- AIRPORT INFORMATION (W5) — the bus-schedule/airport pages carry a redundant dead "AIRPORT
    // INFORMATION" <button> beside the working link; wire it to the served airport-information page so the
    // dead duplicate also routes. Only on mountain pages (code present).
    if (code) {
      for (const btn of Array.from(
        document.querySelectorAll<HTMLElement>("button"),
      )) {
        if (btn.closest("header, footer")) continue;
        if (btn.getAttribute("data-behavior")) continue;
        if ((btn.textContent || "").replace(/\s+/g, " ").trim().toLowerCase() !== "airport information") continue;
        btn.setAttribute("data-behavior", "airport-info-nav");
        btn.style.cursor = "pointer";
        const href = `/mountain-${code}/bus-schedule/airport-information`;
        const go = (e: Event) => {
          e.preventDefault();
          window.location.assign(href);
        };
        btn.addEventListener("click", go);
        cleanups.push(() => btn.removeEventListener("click", go));
      }
    }

    // ---- FOOTER QUICK-LINKS (W5/C7) — dead href="#" footer category links on no-mountain-context pages get
    // their live WHISTLER-default href (matches live; also fixes the link-graph). Idempotent (only touches
    // href="#"). Setting href (not onClick) keeps them real crawlable links.
    for (const a of Array.from(
      document.querySelectorAll<HTMLAnchorElement>('footer a[href="#"], footer a:not([href])'),
    )) {
      const t = (a.textContent || "").replace(/\s+/g, " ").trim();
      const hit = FOOTER_WHISTLER_DEFAULTS.find(([re]) => re.test(t));
      if (!hit) continue;
      a.setAttribute("href", hit[1]);
      a.setAttribute("data-behavior", "footer-quicklink");
    }

    // ---- COOKIE-CONSENT — the captured react-cookie-consent Accept (dead on home/landings/content, where
    // HubChromeLayer is not mounted). Idempotent: skip if already wired (HubChromeLayer tags none, so we use a
    // dedicated marker). Only wired on click; nothing hidden at rest → pixel gate untouched.
    for (const accept of Array.from(
      document.querySelectorAll<HTMLElement>("#rcc-confirm-button"),
    )) {
      if (accept.getAttribute("data-link-cookie") === "1") continue;
      accept.setAttribute("data-link-cookie", "1");
      const onAccept = () => {
        document.cookie =
          "CookieConsent=true; path=/; max-age=31536000; samesite=lax";
        const banner = accept.closest<HTMLElement>(".CookieConsent");
        if (banner) banner.style.display = "none";
      };
      accept.style.cursor = "pointer";
      accept.addEventListener("click", onAccept);
      cleanups.push(() => accept.removeEventListener("click", onAccept));
    }

    return () => cleanups.forEach((c) => c());
  }, []);

  return null;
}
